import { useEffect, useState } from 'react';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import useSelectScenarioContext
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/SelectScenarioContext';
import { removeQueryWithReplace } from 'Helpers/Query';

const STEPS_AFTER_GOALS = [
  'ScenarioSelectionRedefinedTaxStrategyStep',
  'ScenarioSelectionRedefinedTheYearAheadGoalStrategiesDetailsStep',
  'ScenarioSelectionRedefinedPortfolioUpdatesStep',
];

export function useScenarioSelectionRedefinedWrapperTemplate({
  resource,
}) {
  const [errors, setErrors] = useState([]);
  const { setIsSelecting, isSelecting } = useSelectScenarioContext();
  const [loading, setLoading] = useState(false);
  const { logStandardizedEvent, logStandardizedEventFailureWithError } = useEventTrackingLogEvent();
  const { loadPageContentLink, setPageContentCollection, isPageContentLoading } = usePageContentContext();
  const previousLink = resource.relationship(fr.relationships.previousLink.name)?.link('related');
  const templateName = resource.type();

  // This use effect needs to exist so that if we go to the tax strategy detail step after the goal strategy detail step, and then hit back,
  // we land on the last goal.
  useEffect(() => {
    if (STEPS_AFTER_GOALS.includes(templateName)) {
      return;
    }
    removeQueryWithReplace('currentGoalId');
  }, [templateName]);

  const handleNext = async () => {
    logStandardizedEvent(categories.wizard, eventTypes.click, 'continue', { eventProperties: { templateName } });
    setErrors(null);
    setLoading(true);
    try {
      if (resource.isTruthy('shouldExecuteSelect')) {
        setIsSelecting(true);
      }
      const updatedColl = await resource.submit();
      await setPageContentCollection(updatedColl);
    } catch (e) {
      logStandardizedEventFailureWithError(categories.wizard, eventTypes.click, 'continue', e, { eventProperties: { templateName } });
      if (e.errors) {
        setErrors(e.errors);
      } else {
        setErrors(['Failed to continue']);
      }
    } finally {
      setLoading(false);
      setIsSelecting(false);
    }
  };

  const handlePrevious = previousLink ? (
    async () => {
      logStandardizedEvent(categories.wizard, eventTypes.click, 'back', { eventProperties: { templateName } });
      setLoading(true);
      setErrors(null);
      try {
        await loadPageContentLink(previousLink);
      } catch (e) {
        logStandardizedEventFailureWithError(categories.wizard, eventTypes.click, 'back', e, { eventProperties: { templateName } });
        setLoading(false);
        if (e.errors) {
          setErrors(e.errors);
        } else {
          setErrors(['Failed to go back']);
        }
      } finally {
        setLoading(false);
      }
    }
  ) : null;

  return {
    handleNext,
    handlePrevious,
    errors,
    loading,
    isPageContentLoading,
    isSelecting,
  };
}

useScenarioSelectionRedefinedWrapperTemplate.defaultProps = {
};

useScenarioSelectionRedefinedWrapperTemplate.propTypes = {
  /** resource: The displayed resource * */
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};
